import React from 'react';
import { Link } from 'react-router-dom';

export interface IToolBreadcrumbItem {
  label: string;
  // A plain path, or a location descriptor when the target page reads its
  // state from the query string (e.g. a status filter, sort, or page) that
  // needs to be carried over rather than reset, or from router location
  // state (e.g. which list/database the record was opened from).
  to?: string | { pathname: string; search?: string; state?: unknown };
}

interface IToolBreadcrumbProps {
  items: IToolBreadcrumbItem[];
}

const ChevronIcon = () => (
  <svg
    width='12'
    height='12'
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M6 12L10 8L6 4'
      stroke='currentColor'
      strokeWidth='1.33333'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const HomeIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 20 20'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M7.5 17.5V11.3333C7.5 10.8666 7.5 10.6333 7.59083 10.455C7.67072 10.2982 7.79821 10.1707 7.95501 10.0908C8.13327 9.99999 8.36662 9.99999 8.83333 9.99999H11.1667C11.6334 9.99999 11.8667 9.99999 12.045 10.0908C12.2018 10.1707 12.3293 10.2982 12.4092 10.455C12.5 10.6333 12.5 10.8666 12.5 11.3333V17.5M9.18141 2.30333L3.52949 6.69927C3.15168 6.99312 2.96278 7.14005 2.82669 7.32405C2.70614 7.48704 2.61633 7.67065 2.56169 7.86588C2.5 8.08627 2.5 8.32558 2.5 8.80421V14.8333C2.5 15.7667 2.5 16.2335 2.68166 16.59C2.84144 16.9036 3.09641 17.1585 3.41002 17.3183C3.76654 17.5 4.23325 17.5 5.16667 17.5H14.8333C15.7668 17.5 16.2335 17.5 16.59 17.3183C16.9036 17.1585 17.1586 16.9036 17.3183 16.59C17.5 16.2335 17.5 15.7667 17.5 14.8333V8.80421C17.5 8.32558 17.5 8.08627 17.4383 7.86588C17.3837 7.67065 17.2939 7.48704 17.1733 7.32405C17.0372 7.14005 16.8483 6.99312 16.4705 6.69927L10.8186 2.30333C10.5258 2.07562 10.3794 1.96177 10.2178 1.918C10.0752 1.87938 9.92484 1.87938 9.78221 1.918C9.62057 1.96177 9.47418 2.07562 9.18141 2.30333Z'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

// Shared "nested tool" breadcrumb — home icon + tool root, then a chevron-separated
// trail down to the current record, bold and unlinked. Mirrors the Import Hub pattern.
const ToolBreadcrumb: React.FC<IToolBreadcrumbProps> = ({ items }) => (
  <nav className='tool-breadcrumb' aria-label='Breadcrumb'>
    {items.map((item, index) => {
      const isCurrent = index === items.length - 1 || !item.to;
      return (
        <React.Fragment key={`${item.label}-${index}`}>
          {index > 0 && <ChevronIcon />}
          {isCurrent ? (
            <span className='tool-breadcrumb-current' title={item.label}>
              {item.label}
            </span>
          ) : (
            <Link
              to={item.to}
              className='tool-breadcrumb-link'
              title={item.label}
            >
              {index === 0 && <HomeIcon />}
              {item.label}
            </Link>
          )}
        </React.Fragment>
      );
    })}
  </nav>
);

export default ToolBreadcrumb;
